import { visit, SKIP } from 'unist-util-visit';

import type { Element, Root } from 'hast';
const allowedImageDomains = new Set([
  'littlebird.ai',
  'picsum.photos',
  'little-bird-dev-media.s3.amazonaws.com',
  'little-bird-prod-media.s3.amazonaws.com',
  'docs.google.com',
  'mail.google.com',
  'www.youtube.com',
  'www.facebook.com',
  'www.google.com',
  'www.dazn.com',
  'www.leparisien.fr',
  'snickargladje.webkeeper.se',
  'www.linkedin.com',
  'www.boots.com',
  'www.smartflowsheet.com',
  'chatgpt.com',
  'new.express.adobe.com',
  'www.amazon.com',
  'meet.google.com',
  'github.com',
  'web.whatsapp.com',
  'trinityprep.instructure.com',
  'athlyzergmbh.pipedrive.com',
  'gemini.google.com',
  'brightspace.universiteitleiden.nl',
  'app.qargo.com',
  'www.disneyplus.com',
  'app.mymusicstaff.com',
  'app.propstream.com',
  'apex.taicloud.net',
  'www.amazon.co.uk',
  'www.instagram.com',
  'drive.google.com',
  'app.sintra.ai',
  'admin.shopify.com',
  'suno.com',
  'calendar.google.com',
  'business.facebook.com',
  'accounts.google.com',
  'www.perplexity.ai',
  'news.ycombinator.com',
  'www.canva.com',
  'app.base44.com',
  'lovable.dev',
  'www.reddit.com',
  'bolt.new',
  'www.is-intelligentsolutions.com',
  'x.com',
  'secure.helpscout.net',
  'grok.com',
  'www.ecovacs.com',
  'notebooklm.google.com',
  'airtable.com',
  'www.thenorthface.com',
  'ts8.x1.international.travian.com',
  'canvas.andover.edu',
  'www.vodafone.co.uk',
  'www.abercrombie.com',
  'compasssupport.zendesk.com',
  'reactio25.odoo.com',
  'canvas.maastrichtuniversity.nl',
  'ifaw.sharepoint.com',
  'portal.gibbs-gillespie.co.uk',
  'www.portal.is-intelligentsolutions.com',
  'my.sevdesk.de',
  'www.origamis.cz',
  'subtlbeauty.com',
  'lms.uconn.edu',
  'vercel.com',
  'click.endnote.com',
  'www.airbnb.com',
  'gamma.app',
  'lead.randallreillyapi.com',
  'etraining.sja.org.uk',
  'jakubsmolka-snuggs.odoo.com',
  'www.arenacineplex.com',
  'easyweb.td.com',
  'www.redfin.com',
  'app.outbuild.com',
  'invoicehome.com',
  'jxonline.pensplan.com',
  'www.manualslib.com',
  'app.krisp.ai',
  'viewer.autodesk.com',
  'gitlab.com',
  'app.hubspot.com',
  'find-and-update.company-information.service.gov.uk',
  'www.cnet.com',
  'www.chess.com',
  'pensplan.com',
  'www.marriott.com',
  'invoice.stripe.com',
  'path.globalmediaoutreach.com',
  'phpstack-819796-5178277.cloudwaysapps.com',
  'app.monarchmoney.com',
  'www.airbnb.dk',
  'www.zillow.com',
  'ebookcentral.proquest.com',
]);

/**
 * Parse srcset attribute value into individual URLs
 * Handles data: URIs which contain commas
 */
function parseSrcSet(srcSet: string): string[] {
  const urls: string[] = [];
  let currentUrl = '';
  let inDataUri = false;

  for (let i = 0; i < srcSet.length; i++) {
    const char = srcSet[i];

    if (!inDataUri && srcSet.substring(i, i + 5) === 'data:') {
      inDataUri = true;
    }

    if (char === ',' && !inDataUri) {
      if (currentUrl.trim()) {
        // Extract just the URL part (before any descriptors like "2x" or "100w")
        // Ex: https://example.com/image@2x.png 2x
        const url = currentUrl.trim().split(/\s+/)[0];
        urls.push(url);
      }
      currentUrl = '';
    } else {
      currentUrl += char;

      if (inDataUri && /\s/.test(char)) {
        inDataUri = false;
      }
    }
  }

  // the last URL
  if (currentUrl.trim()) {
    const url = currentUrl.trim().split(/\s+/)[0];
    urls.push(url);
  }

  return urls;
}

export function isAllowedImageUrl(url: string) {
  url = url.trim();

  if (!url) {
    return false;
  }

  // Normalize backslashes to forward
  // because browsers do this too
  url = url.replace(/\\/g, '/');

  if (url.startsWith('./')) {
    return true;
  }

  if (url.startsWith('/') && !url.startsWith('//')) {
    return true;
  }

  try {
    const parsedUrl = new URL(url);

    if (parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:') {
      return allowedImageDomains.has(parsedUrl.hostname);
    }

    if (parsedUrl.protocol === 'blob:') {
      if (parsedUrl.origin === globalThis.location?.origin) {
        return true;
      }

      const blobURL = url.slice(5);

      try {
        const originParsed = new URL(blobURL);
        if (
          originParsed.protocol === 'http:' ||
          originParsed.protocol === 'https:'
        ) {
          return allowedImageDomains.has(originParsed.hostname);
        }
      } catch (_e) {
        return false;
      }
    }

    // Allow safe data: URIs for raster images only
    // SVG data URIs are blocked because they can contain <image>, <script>,
    // or <foreignObject> tags that fetch external resources, bypassing the allowlist
    if (parsedUrl.protocol === 'data:') {
      // Only allow base64-encoded raster image formats
      const safeImageFormats =
        /^data:image\/(png|jpeg|jpg|gif|webp|bmp|ico);base64,/i;
      return safeImageFormats.test(url);
    }

    return false;
  } catch (_e) {
    return false;
  }
}

function remove(
  parent: Root | Element | undefined,
  index: number | undefined
): typeof SKIP | [typeof SKIP, number] {
  if (parent && typeof index === 'number') {
    parent.children.splice(index, 1);
    return [SKIP, index];
  }
  return SKIP;
}

export default function imgSanitizer() {
  return function (tree: Root) {
    visit(
      tree,
      'element',
      function (
        node: Element,
        index: number | undefined,
        parent: Root | Element | undefined
      ) {
        if (node.tagName === 'img') {
          const src = node.properties?.src;
          if (typeof src === 'string') {
            if (!isAllowedImageUrl(src)) {
              return remove(parent, index);
            }
          }
          const srcSet = node.properties?.srcSet;
          if (typeof srcSet === 'string') {
            const srcsetUrls = parseSrcSet(srcSet);
            const srcsetAllowed = srcsetUrls.every(isAllowedImageUrl);
            if (!srcsetAllowed) {
              return remove(parent, index);
            }
          }
          // Image is allowed, skip visiting its children but continue with siblings
          return SKIP;
        } else if (node.tagName === 'source') {
          // to support,
          //   <picture>
          //     <source ... />
          //  </picture>
          const srcSet = node.properties?.srcSet;
          if (typeof srcSet === 'string') {
            const srcsetUrls = parseSrcSet(srcSet);
            const srcsetAllowed = srcsetUrls.every(isAllowedImageUrl);
            if (!srcsetAllowed) {
              return remove(parent, index);
            }
          }
          // Image is allowed, skip visiting its children but continue with siblings
          return SKIP;
        }
      }
    );
  };
}
